import React, { useState, useEffect } from 'react';
import { firestore } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import VisitasChart from '../Gestor/VisitasChart';
import EditModal from '../Gestor/EditModal';

// Função Helper para normalizar texto (apenas remove espaços extras e caixa alta)
const normalizarTexto = (texto) => {
    if (!texto) return '';
    return texto.trim().toUpperCase();
};

function DashboardDiarioSupervisor({ userLocalidades, user }) {
    const [visitas, setVisitas] = useState([]);
    const [filteredVisitas, setFilteredVisitas] = useState([]);
    const [loading, setLoading] = useState(false); // Começa false
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [filterAgente, setFilterAgente] = useState('');
    const [filterQuarteirao, setFilterQuarteirao] = useState('');
    const [filterLocalidade, setFilterLocalidade] = useState('');
    const [agentsMap, setAgentsMap] = useState({});

    // Buscar agentes
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const q = query(collection(firestore, "usuarios"), where("perfil", "==", "agente"));
                const querySnapshot = await getDocs(q);
                const agents = {};

                querySnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.email) {
                        agents[data.email.toLowerCase()] = data.nome || data.email;
                    }
                    agents[doc.id] = data.nome || data.email;
                });

                setAgentsMap(agents);
            } catch (error) {
                console.error("Erro ao buscar agentes: ", error);
            }
        };
        fetchAgents();
    }, []);

    // ==============================
    // 🔹 Buscar visitas diárias (Manual + Filtro Server-Side)
    // ==============================
    const fetchVisitasSupervisor = async () => {
        if (!userLocalidades || userLocalidades.length === 0) {
            setLoading(false);
            alert("Nenhuma localidade atribuída."); // Alerta visual
            return;
        }

        setLoading(true);
        try {
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);

            // Garantir que as localidades estejam em MAIÚSCULAS para bater com o banco
            const localidadesUpper = userLocalidades.map(loc => normalizarTexto(loc));

            // Firestore 'in' supporta max 10 valores. Precisamos fazer em lotes (chunks).
            const chunks = [];
            for (let i = 0; i < localidadesUpper.length; i += 10) {
                chunks.push(localidadesUpper.slice(i, i + 10));
            }

            const promises = chunks.map(chunk => {
                const q = query(
                    collection(firestore, 'relatoriosDiarios_FAD1'),
                    where('dataAtividade', '>=', startOfDay),
                    where('dataAtividade', '<=', endOfDay),
                    where('localidade', 'in', chunk) // Filtro Server-Side Restaurado!
                );
                return getDocs(q);
            });

            const snapshots = await Promise.all(promises);
            let todasVisitas = [];
            snapshots.forEach(snap => {
                const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                todasVisitas = [...todasVisitas, ...data];
            });

            // Filtrar visitas canceladas (ROBUSTO)
            todasVisitas = todasVisitas.filter(v => !v.statusRegistro || v.statusRegistro.trim().toLowerCase() === 'ativo');

            setVisitas(todasVisitas);

        } catch (error) {
            console.error("Erro ao buscar dados diários (Supervisor): ", error);
            alert("Erro ao buscar dados. Verifique sua conexão.");
        } finally {
            setLoading(false);
        }
    };

    // REMOVIDO: useEffect que carregava automaticamente


    // Aplicar filtros adicionais
    useEffect(() => {
        let result = visitas;

        if (filterAgente) {
            result = result.filter(v => {
                const agenteKey = v.idAgente || v.emailAgente || '';
                const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                // Usa normalizarTexto para busca insensível a acentos
                return normalizarTexto(nomeAgente).includes(normalizarTexto(filterAgente));
            });
        }

        if (filterQuarteirao) {
            result = result.filter(v => v.quarteirao && v.quarteirao.toString().includes(filterQuarteirao));
        }

        if (filterLocalidade) {
            result = result.filter(v => v.localidade && normalizarTexto(v.localidade).includes(normalizarTexto(filterLocalidade)));
        }

        setFilteredVisitas(result);
    }, [visitas, filterAgente, filterQuarteirao, filterLocalidade, agentsMap]);

    const handleOpenModal = (visitaDaLinha) => {
        setSelectedVisit(visitaDaLinha);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedVisit(null);
    };

    const handleSaveSuccess = () => {
        handleCloseModal();
    };

    // Alerta se não tem localidades atribuídas
    if (!userLocalidades || userLocalidades.length === 0) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <h3>⚠️ Nenhuma localidade atribuída</h3>
                <p style={{ color: '#666' }}>
                    Entre em contato com o gestor para atribuir localidades ao seu perfil.
                </p>
            </div>
        );
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>Atendimentos Realizados Hoje ({new Date().toLocaleDateString('pt-BR')})</h3>
                <button
                    onClick={fetchVisitasSupervisor}
                    disabled={loading}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1
                    }}
                >
                    {loading ? 'Carregando...' : 'Atualizar Dados'}
                </button>
            </div>
            <p style={{ color: '#666', marginBottom: '20px' }}>
                Mostrando dados das localidades: <strong>{userLocalidades.join(', ')}</strong>
            </p>

            <div className="dashboard-summary">
                <div className="summary-card" style={{ maxHeight: '300px', overflowY: 'auto', padding: '15px' }}>
                    <h4 style={{ fontSize: '14px', marginBottom: '15px', marginTop: '5px' }}>Resumo por Localidade</h4>
                    {filteredVisitas.length === 0 ? (
                        <div style={{ fontSize: '14px' }}>0</div>
                    ) : (
                        <div style={{ textAlign: 'left', width: '100%', paddingTop: '5px' }}>
                            {Object.entries(filteredVisitas.reduce((acc, curr) => {
                                const loc = curr.localidade || 'Sem Localidade';
                                acc[loc] = (acc[loc] || 0) + 1;
                                return acc;
                            }, {})).sort((a, b) => b[1] - a[1]).map(([loc, count]) => (
                                <div key={loc} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px', paddingBottom: '2px', borderBottom: '1px solid #f0f0f0' }}>
                                    <div style={{ color: '#444' }}>{loc}:</div>
                                    <div style={{ fontWeight: 'bold' }}>{count}</div>
                                </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 'bold', marginTop: '8px', paddingTop: '6px', borderTop: '2px solid #ddd' }}>
                                <div>Total Geral:</div>
                                <div>{filteredVisitas.length}</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="summary-card chart-card">
                    <h4>Visitas por Agente</h4>
                    {Object.keys(agentsMap).length > 0 ? (
                        <VisitasChart visitas={filteredVisitas} agentsMap={agentsMap} />
                    ) : (
                        <p>Carregando dados do gráfico...</p>
                    )}
                </div>
            </div>

            <div className="filters-container">
                <input
                    type="text"
                    placeholder="Filtrar por nome do agente..."
                    value={filterAgente}
                    onChange={(e) => setFilterAgente(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Filtrar por quarteirão..."
                    value={filterQuarteirao}
                    onChange={(e) => setFilterQuarteirao(e.target.value)}
                />
                <select
                    value={filterLocalidade}
                    onChange={(e) => setFilterLocalidade(e.target.value)}
                    style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                    <option value="">Todas as Localidades</option>
                    {userLocalidades && userLocalidades.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <p>Carregando dados...</p>
            ) : (
                <div className="table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Horário</th>
                                <th>Agente</th>
                                <th>Localidade</th>
                                <th>Quarteirão</th>
                                <th>Nº</th>
                                <th>Comp.</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVisitas.map(v => {
                                const agenteKey = v.idAgente || v.emailAgente || '';
                                const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                                return (
                                    <tr key={v.id}>
                                        <td>{v.dataAtividade?.toDate?.().toLocaleTimeString('pt-BR') || '—'}</td>
                                        <td>{nomeAgente}</td>
                                        <td>{v.localidade || '—'}</td>
                                        <td>{v.quarteirao || '—'}</td>
                                        <td>{v.numeroImovel || 'N/A'}</td>
                                        <td>{v.complemento || '—'}</td>
                                        <td>
                                            <span className={`status status-${v.status?.replace(' ', '-').toLowerCase()}`}>
                                                {v.status || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="view-button" onClick={() => handleOpenModal(v)}>
                                                Visualizar
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <EditModal
                    visita={selectedVisit}
                    agentsMap={agentsMap}
                    onClose={handleCloseModal}
                    onSaveSuccess={handleSaveSuccess}
                    currentUser={user}
                />
            )}
        </>
    );
}

export default DashboardDiarioSupervisor;
